import { Button } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { useTakePaymentMutation, useTakePaymentV2Mutation, useUpdatePaymentV2Mutation } from '~/services/member';
import { toast } from 'sonner';
import { useGetLocationId } from '~/modules/auth';
import { TakePaymentFormData } from '../../../../helpers/TakePaymentSchema';
import useTakePaymentStore from '../../../../hooks/useTakePaymentStore';
import { roundNumber } from '~/modules/@shared/helpers/roundNumber';
import { GAEventPath, GASendEvent } from '~/analytics';
import { APIResponse, EditPaymentV2Payload, TakePaymentPayload, TakePaymentV2Payload } from '~/services/types';

export default function ProcessChargeButton() {
  const { locationId } = useGetLocationId();
  const { mutateAsync: takePayment, isPending: isPendingV1 } = useTakePaymentMutation({ locationId });
  const { mutateAsync: takePaymentV2, isPending: isPendingV2 } = useTakePaymentV2Mutation({ locationId });
  const { mutateAsync: updatePaymentV2, isPending: isPendingUpdate } = useUpdatePaymentV2Mutation({
    locationId,
  });

  const clean = useTakePaymentStore((state) => state.clean);

  const { handleSubmit, reset } = useFormContext<TakePaymentFormData>();

  const handleProcessCharge = async (formData: TakePaymentFormData) => {
    try {
      const request: TakePaymentPayload | TakePaymentV2Payload = {
        amount: roundNumber({ value: formData.amount, decimals: 2 }),
        description: formData.description ?? null,
        location_id: locationId,
        member_id: formData.member_id!, // It is safe to assume that member_id is always set because of the schema
        payment_method_id: formData.payment_method_id,
        scheduled_date: formData.scheduled_date,
        taxable: formData.isDea != true ? formData.taxable : false,
      };

      if (formData.payment_id) {
        (request as EditPaymentV2Payload).payment_id = formData.payment_id;
      }

      let response: APIResponse;
      if (!formData.isDea) {
        (request as TakePaymentPayload).payment_category_type_id = formData.payment_category_type_id ?? 1;
        response = await takePayment(request as TakePaymentPayload);
      } else {
        if (formData.payment_id) {
          response = await updatePaymentV2(request as EditPaymentV2Payload);
        } else {
          response = await takePaymentV2(request as TakePaymentV2Payload);
        }
      }

      if (response.status === 'CREATED' || response.status === 'UPDATED') {
        GASendEvent(GAEventPath.SUCCESS_FORM_SUBMIT, 'TakeAPayment');

        reset();
        clean();
      } else {
        const message = response.message ?? 'Unknown Error';

        GASendEvent(GAEventPath.ERROR_FORM_SUBMIT, `TakeAPayment - ${message}`);
        throw new Error(message);
      }
    } catch (e) {
      const error = e as Error;
      const message = `Please check your data - ${error.message || 'Unknown Error'}`;

      GASendEvent(GAEventPath.ERROR_FORM_SUBMIT, `TakeAPayment - ${message}`);

      toast.error(message);
    }
  };

  return (
    <Button
      disabled={isPendingUpdate || isPendingV1 || isPendingV2}
      variant="contained"
      size="large"
      onClick={() => {
        void handleSubmit(handleProcessCharge)();
      }}
      sx={{ width: '200px' }}>
      Process Payment
    </Button>
  );
}
